<template>
  <div class="repair-pool-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('repairPoolManage.repairOrderPool')" :subtitle="$t('repairPoolManage.page.subtitle')" />

    <div class="repair-pool-layout">
      <!-- 左侧状态栏 -->
      <div class="ds-panel ds-panel--states">
        <div class="ds-panel__header">
          <span class="ds-panel__title">{{ $t('repairPoolManage.page.stateTitle') }}</span>
        </div>
        <div class="status-list">
          <button type="button" v-for="(item, index) in states" :key="index" class="status-item"
            :class="{ 'status-active': conditions.state === item.statusCd }" @click="switchRepairState(item)">
            {{ item.name }}
          </button>
        </div>
      </div>

      <div class="repair-pool-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.repairId" clearable class="w-full"
                :placeholder="$t('repairPoolManage.workOrderNumber')" @keyup.enter.native="queryRepairPool" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.repairName" clearable class="w-full"
                :placeholder="$t('repairPoolManage.repairPersonName')" @keyup.enter.native="queryRepairPool" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.tel" clearable class="w-full"
                :placeholder="$t('repairPoolManage.repairPhone')" @keyup.enter.native="queryRepairPool" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.repairType" clearable class="w-full"
                :placeholder="$t('repairPoolManage.selectRepairType')">
                <el-option v-for="(item, index) in repairSettings" :key="index" :label="item.repairTypeName"
                  :value="item.repairType" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.repairObjName" clearable class="w-full"
                :placeholder="$t('repairPoolManage.repairLocation')" @keyup.enter.native="queryRepairPool" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.maintenanceType" clearable class="w-full"
                :placeholder="$t('repairPoolManage.selectMaintenanceType')">
                <el-option v-for="(item, index) in maintenanceTypes" :key="index" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.startTime" type="date" class="w-full" value-format="yyyy-MM-dd"
                :placeholder="$t('repairPoolManage.startTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.endTime" type="date" class="w-full" value-format="yyyy-MM-dd"
                :placeholder="$t('repairPoolManage.endTime')" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="queryRepairPool">
              {{ $t('repairPoolManage.search') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetRepairPool">
              {{ $t('repairPoolManage.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="fetchData">
            <template #left>
              <span class="ds-panel__title">{{ $t('repairPoolManage.repairOrderPool') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="repairPools" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('repairPoolManage.page.empty')" @retry="fetchData">
            <el-table-column prop="repairId" :label="$t('repairPoolManage.workOrderCode')" align="center" width="150">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.repairId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="repairObjName" :label="$t('repairPoolManage.location')" align="center" min-width="140"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.repairObjName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="repairTypeName" :label="$t('repairPoolManage.repairType')" align="center" width="120">
              <template slot-scope="scope">
                {{ scope.row.repairTypeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('repairPoolManage.maintenanceType')" align="center" width="120">
              <template slot-scope="scope">
                {{ formatMaintenanceType(scope.row.maintenanceType) }}
              </template>
            </el-table-column>
            <el-table-column prop="repairName" :label="$t('repairPoolManage.repairPerson')" align="center" width="100">
              <template slot-scope="scope">
                {{ scope.row.repairName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="tel" :label="$t('repairPoolManage.contact')" align="center" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.tel || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('repairPoolManage.submissionTime')" align="center" width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="submitHours" :label="$t('repairPoolManage.submissionDuration')" align="center" width="110">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.submitHours || '0' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="finishTime" :label="$t('repairPoolManage.completionTime')" align="center" width="160">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.finishTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('repairPoolManage.status')" align="center" width="140">
              <template slot-scope="scope">
                <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName"
                  :type="getStateType(scope.row.state)" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('repairPoolManage.operation')" align="center" width="280" fixed="right">
              <template slot-scope="scope">
                <el-button v-if="hasPrivilege('502021012067300023') && ['1000', '1200'].includes(scope.row.state)"
                  size="mini" @click="openEditModal(scope.row)">
                  {{ $t('repairPoolManage.modify') }}
                </el-button>

                <el-button v-if="hasPrivilege('502019101946430010') && scope.row.state === '1000'" size="mini"
                  type="primary" @click="openDispatchModal(scope.row)">
                  {{ $t('repairPoolManage.dispatch') }}
                </el-button>

                <el-button
                  v-if="hasPrivilege('502021012099350016') && scope.row.state === '1000' && scope.row.repairWay === '100'"
                  size="mini" type="success" @click="grabOrder(scope.row)">
                  {{ $t('repairPoolManage.grabOrder') }}
                </el-button>

                <el-button
                  v-if="hasPrivilege('502019101946430010') && !['1900', '1700', '1800'].includes(scope.row.state)"
                  size="mini" type="warning" @click="openEndModal(scope.row)">
                  {{ $t('repairPoolManage.end') }}
                </el-button>

                <el-button size="mini" type="info" @click="openDetail(scope.row)">
                  {{ $t('repairPoolManage.details') }}
                </el-button>

                <el-button v-if="hasPrivilege('502021012051410024') && ['1000', '1200'].includes(scope.row.state)"
                  size="mini" type="danger" @click="openDeleteModal(scope.row.repairId)">
                  {{ $t('repairPoolManage.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
            @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </div>
    </div>

    <!-- 组件 -->
    <dispatch-repair ref="dispatchRepair" @success="fetchData" />
    <end-repair ref="endRepair" @success="fetchData" />
    <edit-owner-repair ref="editOwnerRepair" @success="fetchData" />
    <delete-owner-repair ref="deleteOwnerRepair" @success="fetchData" />
  </div>
</template>

<script>
import { listRepairPools, grabbingRepair } from '@/api/work/repairPoolManageApi'
import { getDict } from '@/api/community/communityApi'
import { getCommunityId } from '@/api/community/communityApi'
import DispatchRepair from '@/components/work/dispatchRepair'
import EndRepair from '@/components/work/endRepair'
import EditOwnerRepair from '@/components/work/editOwnerRepair'
import DeleteOwnerRepair from '@/components/work/deleteOwnerRepair'
import { listRepairSettings } from '@/api/work/ownerRepairManageApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'RepairPoolManage',
  components: {
    DispatchRepair,
    EndRepair,
    EditOwnerRepair,
    DeleteOwnerRepair,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      repairPools: [],
      repairSettings: [],
      states: [],
      repairSettingTypes: [],
      maintenanceTypes: [],
      moreCondition: false,
      conditions: {
        repairId: '',
        repairName: '',
        tel: '',
        repairType: '',
        repairSettingType: '',
        roomId: '',
        roomName: '',
        ownerId: '',
        state: '',
        maintenanceType: '',
        startTime: '',
        endTime: '',
        repairObjName: ''
      },
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.fetchData()
    this.fetchRepairSettings()
    this.loadDictData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          ...this.conditions,
          page: this.page.current,
          row: this.page.size
        }

        const res = await listRepairPools(params)
        this.repairPools = res.data || []
        this.page.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('repairPoolManage.page.fetchError')
      } finally {
        this.loading = false
      }
    },

    async fetchRepairSettings() {
      try {
        const params = {
          page: 1,
          row: 50,
          communityId: getCommunityId()
        }

        const res = await listRepairSettings(params)
        this.repairSettings = res.data || []
      } catch (error) {
        console.error('Failed to fetch repair settings:', error)
      }
    },

    async loadDictData() {
      try {
        // 获取状态字典
        const stateData = await getDict('r_repair_pool', 'state')
        this.states = [
          { statusCd: '', name: this.$t('repairPoolManage.all') },
          // 超时工单筛选项已移除
          ...(stateData || [])
        ]

        // 获取报修设置类型字典
        this.repairSettingTypes = await getDict('r_repair_setting', 'repair_setting_type')

        // 获取维修类型字典
        this.maintenanceTypes = await getDict('r_repair_pool', 'maintenance_type')
      } catch (error) {
        console.error('Failed to load dict data:', error)
      }
    },

    formatMaintenanceType(type) {
      const types = {
        '1001': this.$t('repairPoolManage.paidService'),
        '1002': this.$t('repairPoolManage.freeService'),
        '1003': this.$t('repairPoolManage.needMaterials'),
        '1004': this.$t('repairPoolManage.noMaterialsNeeded')
      }
      return types[type] || '--'
    },

    getStateType(state) {
      switch (state) {
        case '1000': return 'info'
        case '1800': return 'warning'
        case '1900': return 'success'
        case '2001': return 'muted'
        default: return 'info'
      }
    },

    switchRepairState(item) {
      this.conditions.state = item.statusCd
      this.page.current = 1
      this.fetchData()
    },

    toggleMoreCondition() {
      this.moreCondition = !this.moreCondition
    },

    queryRepairPool() {
      this.page.current = 1
      this.fetchData()
    },

    resetRepairPool() {
      this.conditions = {
        repairId: '',
        repairName: '',
        tel: '',
        repairType: '',
        repairSettingType: '',
        repairObjName: '',
        maintenanceType: '',
        startTime: '',
        endTime: '',
        state: this.conditions.state // 保持当前状态
      }
      this.page.current = 1
      this.fetchData()
    },

    openEditModal(repair) {
      this.$refs.editOwnerRepair.open(repair)
    },

    openDispatchModal(repair) {
      this.$refs.dispatchRepair.open(repair)
    },

    async grabOrder(repair) {
      try {
        const data = {
          communityId: getCommunityId(),
          repairId: repair.repairId
        }

        await grabbingRepair(data)
        this.$message.success(this.$t('common.operationSuccess'))
        this.fetchData()
      } catch (error) {
        console.error('Grab order failed:', error)
      }
    },

    openEndModal(repair) {
      this.$refs.endRepair.open(repair)
    },

    openDetail(repair) {
      this.$router.push(`/views/work/repairDetail?repairId=${repair.repairId}`)
    },

    openDeleteModal(repairId) {
      this.$refs.deleteOwnerRepair.open({ repairId })
    },

    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.fetchData()
    },

    handleCurrentChange(current) {
      this.page.current = current
      this.fetchData()
    },
  }
}
</script>

<style lang="scss" scoped>
.repair-pool-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧状态 + 右侧内容 */
.repair-pool-layout {
  display: flex;
  align-items: flex-start;
  gap: var(--ds-spacing-lg);
}

.repair-pool-main {
  flex: 1;
  min-width: 0;
}

/* 左侧状态面板 */
.ds-panel--states {
  width: 180px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-base);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 状态列表 */
.status-list {
  display: flex;
  flex-direction: column;
  padding: var(--ds-spacing-sm);
}

.status-item {
  padding: var(--ds-spacing-sm) var(--ds-spacing-md);
  margin-bottom: var(--ds-spacing-xs);
  border: none;
  border-radius: var(--ds-radius-sm);
  background: transparent;
  color: var(--ds-gray-700);
  font-size: var(--ds-font-size-sm);
  text-align: center;
  cursor: pointer;
  transition: background-color var(--ds-transition-base), color var(--ds-transition-base);

  &:hover {
    background-color: var(--ds-gray-100);
  }
}

.status-active {
  background-color: var(--ds-primary);
  color: #fff;

  &:hover {
    background-color: var(--ds-primary);
  }
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}

@media (max-width: 1200px) {
  .repair-pool-layout {
    flex-direction: column;
  }

  .ds-panel--states {
    width: 100%;
  }

  .status-list {
    flex-direction: row;
    flex-wrap: wrap;
  }
}
</style>
